import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatPrice, formatDateTime, localized, apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { VendorEditDialog } from "@/components/VendorEditDialog";
import { Pencil, Trash2 } from "lucide-react";

export default function AdminPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [emails, setEmails] = useState([]);
  const [editingVendor, setEditingVendor] = useState(null);

  const load = async () => {
    try { setStats((await api.get("/admin/stats")).data); } catch (e) { console.error("admin/stats:", e); }
    try { setVendors((await api.get("/admin/vendors")).data); } catch (e) { console.error("admin/vendors:", e); }
    try { setProducts((await api.get("/admin/products")).data); } catch (e) { console.error("admin/products:", e); }
    try { setReviews((await api.get("/admin/reviews")).data); } catch (e) { console.error("admin/reviews:", e); }
    try { setEmails((await api.get("/admin/emails")).data); } catch (e) { console.error("admin/emails:", e); }
  };
  useEffect(() => { load(); }, []);

  const approveVendor = async (id) => { await api.post(`/admin/vendors/${id}/approve`); toast.success("✓"); load(); };
  const rejectVendor = async (id) => { await api.post(`/admin/vendors/${id}/reject`); toast.success("✓"); load(); };
  const deleteVendor = async (id, name) => {
    if (!window.confirm(t("admin.confirmDeleteVendor", { name }))) return;
    try {
      await api.delete(`/admin/vendors/${id}`);
      toast.success(t("admin.vendorDeleted"));
      load();
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
    }
  };
  const flagProduct = async (id) => { await api.post(`/admin/products/${id}/flag`); toast.success("✓"); load(); };
  const restoreProduct = async (id) => { await api.post(`/admin/products/${id}/restore`); toast.success("✓"); load(); };
  const removeReview = async (id) => { await api.post(`/admin/reviews/${id}/remove`); toast.success("✓"); load(); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-panel">
      <p className="overline">{t("admin.overline")}</p>
      <h1 className="font-serif text-5xl tracking-tight mt-2">{t("admin.title")}</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-10" data-testid="admin-stats">
          {[
            [t("admin.statUsers"), stats.users],
            [t("admin.statPendingVendors"), stats.vendors_pending],
            [t("admin.statApproved"), stats.vendors_approved],
            [t("admin.statProducts"), stats.products],
            [t("admin.statOrders"), stats.orders],
            [t("admin.statFlaggedReviews"), stats.reviews_flagged],
          ].map(([l, v]) => (
            <div key={l} className="bg-white hairline rounded-sm p-4">
              <p className="overline">{l}</p>
              <p className="font-serif text-2xl mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="vendors" className="mt-12">
        <TabsList className="bg-transparent border-b border-edge rounded-none h-auto p-0 w-full justify-start overflow-x-auto">
          <TabsTrigger value="vendors" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-vendors">{t("admin.tabVendors")}</TabsTrigger>
          <TabsTrigger value="products" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-products">{t("admin.tabProducts")}</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-reviews">{t("admin.tabReviews")}</TabsTrigger>
          <TabsTrigger value="emails" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-emails">{t("admin.tabEmails")}</TabsTrigger>
        </TabsList>

        <TabsContent value="vendors" className="pt-10">
          <div className="space-y-4" data-testid="admin-vendors-list">
            {vendors.map((v) => (
              <div key={v.vendor_id} className="bg-white hairline rounded-sm p-6 flex justify-between items-center flex-wrap gap-4" data-testid={`admin-vendor-${v.vendor_id}`}>
                <div className="min-w-0">
                  <p className="overline">{localized(v, "region", lang)} · {v.status}</p>
                  <h3 className="font-serif text-2xl mt-1">{localized(v, "farm_name", lang)}</h3>
                  <p className="text-sm text-ink-soft mt-2 max-w-xl line-clamp-2">{localized(v, "story", lang)}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => setEditingVendor(v)} variant="outline" className="rounded-sm" data-testid={`edit-vendor-${v.vendor_id}`}>
                    <Pencil className="w-3 h-3 mr-1" />{t("admin.edit")}
                  </Button>
                  {v.status !== "approved" && <Button onClick={() => approveVendor(v.vendor_id)} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid={`approve-${v.vendor_id}`}>{t("admin.approve")}</Button>}
                  {v.status !== "rejected" && <Button onClick={() => rejectVendor(v.vendor_id)} variant="outline" className="rounded-sm border-terracotta text-terracotta hover:bg-terracotta hover:text-bone" data-testid={`reject-${v.vendor_id}`}>{t("admin.reject")}</Button>}
                  <Button onClick={() => deleteVendor(v.vendor_id, v.farm_name)} variant="outline" className="rounded-sm border-terracotta text-terracotta hover:bg-terracotta hover:text-bone" data-testid={`delete-vendor-${v.vendor_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="products" className="pt-10">
          <div className="space-y-3" data-testid="admin-products-list">
            {products.map((p) => (
              <div key={p.product_id} className="bg-white hairline rounded-sm p-4 flex justify-between items-center flex-wrap gap-2">
                <div>
                  <p className="overline">{p.vendor_name} · {p.status}</p>
                  <h3 className="text-base mt-1">{localized(p, "title", lang)} <span className="text-ink-muted text-sm">— {formatPrice(p.price, lang)}</span></h3>
                </div>
                {p.status === "active" ? (
                  <Button onClick={() => flagProduct(p.product_id)} variant="outline" className="rounded-sm" data-testid={`flag-${p.product_id}`}>{t("admin.flag")}</Button>
                ) : (
                  <Button onClick={() => restoreProduct(p.product_id)} className="bg-olive text-bone rounded-sm" data-testid={`restore-${p.product_id}`}>{t("admin.restore")}</Button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="pt-10">
          <div className="space-y-3" data-testid="admin-reviews-list">
            {reviews.map((r) => (
              <div key={r.review_id} className="bg-white hairline rounded-sm p-4 flex justify-between items-center flex-wrap gap-2">
                <div className="max-w-xl">
                  <p className="overline">{r.user_name} · {r.rating}★ · {r.status}</p>
                  <p className="text-sm mt-1">{r.comment}</p>
                </div>
                {r.status !== "removed" && (
                  <Button onClick={() => removeReview(r.review_id)} variant="outline" className="rounded-sm border-terracotta text-terracotta" data-testid={`remove-review-${r.review_id}`}>{t("admin.remove")}</Button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="emails" className="pt-10">
          <div className="space-y-2" data-testid="admin-emails-list">
            {emails.map((e) => (
              <div key={e.id} className="bg-white hairline rounded-sm p-4" data-testid={`email-${e.id}`}>
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div className="max-w-2xl">
                    <p className="overline">{e.template} · to {e.to}</p>
                    <p className="font-medium text-sm mt-1">{e.subject}</p>
                    <p className="text-xs text-ink-soft mt-1">{e.body}</p>
                  </div>
                  <p className="text-xs text-ink-muted">{formatDateTime(e.created_at, lang)}</p>
                </div>
              </div>
            ))}
            {emails.length === 0 && <p className="text-ink-muted">{t("admin.noEmails")}</p>}
            <p className="text-xs text-ink-muted mt-4 italic">{t("admin.emailsMockedNote")}</p>
          </div>
        </TabsContent>
      </Tabs>

      <VendorEditDialog
        open={!!editingVendor}
        onOpenChange={(o) => !o && setEditingVendor(null)}
        vendor={editingVendor}
        endpoint={editingVendor ? `/admin/vendors/${editingVendor.vendor_id}` : ""}
        allowStatus={true}
        onSaved={load}
      />
    </div>
  );
}
